import PurchaseOrderStateEnum from "@models/PurchaseOrderStateEnum";
import { FilterValues, IFilterField, IFilterSection, IFilterSelectOption } from "@models/IFilterConfig";

export interface IPurchaseOrdersFilterLookups {
    vendorSources: (text: string) => Promise<IFilterSelectOption[]>;
    resolveVendorSource?: (guid: string) => Promise<IFilterSelectOption | undefined>;
}

export interface IPurchaseOrdersFilterContext {
    lookups: IPurchaseOrdersFilterLookups;
    /** Translate a message id (used for static option labels) */
    formatMessage: (id: string) => string;
}

/** Baseline Purchase Orders filters: the legacy search has no default filter (Reset clears everything). */
export const PURCHASE_ORDERS_DEFAULT_FILTERS: FilterValues = {};

/** PO statuses offered by the legacy Purchase Orders search, in its order (translation ids). */
export const PURCHASE_ORDER_STATUS_OPTIONS: { state: PurchaseOrderStateEnum; title: string }[] = [
    { state: PurchaseOrderStateEnum.PRELIMINARY, title: "preliminary" },
    { state: PurchaseOrderStateEnum.SUBMITTED, title: "submitted" },
    { state: PurchaseOrderStateEnum.IN_PROGRESS, title: "in_progress" },
    { state: PurchaseOrderStateEnum.CONFIRMED, title: "confirmed" },
    { state: PurchaseOrderStateEnum.RECEIVED, title: "received" },
    { state: PurchaseOrderStateEnum.INCOMING, title: "incoming" },
    { state: PurchaseOrderStateEnum.ACCEPTED, title: "accepted" },
    { state: PurchaseOrderStateEnum.SHIPPED, title: "shipped" },
];

/**
 * Compile the Purchase Orders filter config. Field keys ARE the wire names (apiKey) of the
 * `getPurchaseOrders` queryString: name, vendorSourceGuid, order, vendor_confirmation_no, po_state_id, is_emailed.
 * Sorting (`order_by` / `order_dir`) and pagination are appended by the screen, not filters.
 */
export function buildPurchaseOrdersFilterSections(ctx: IPurchaseOrdersFilterContext): IFilterSection[] {
    const { formatMessage } = ctx;

    const main: IFilterField[] = [
        { key: "name", label: "name", type: "text", apiKey: "name" },
        {
            key: "vendorSourceGuid",
            label: "vendor_source",
            type: "typeahead",
            apiKey: "vendorSourceGuid",
            lookup: ctx.lookups.vendorSources,
            resolveOption: ctx.lookups.resolveVendorSource,
        },
        { key: "order", label: "order", type: "text", apiKey: "order" },
        { key: "vendor_confirmation_no", label: "vendor_confirmation_no", type: "text", apiKey: "vendor_confirmation_no" },
        {
            key: "po_state_id",
            label: "state",
            type: "select",
            apiKey: "po_state_id",
            options: [
                { label: formatMessage("all"), value: "" },
                ...PURCHASE_ORDER_STATUS_OPTIONS.map((s) => ({ label: formatMessage(s.title), value: String(s.state) })),
            ],
        },
        {
            key: "is_emailed",
            label: "emailed",
            type: "select",
            apiKey: "is_emailed",
            options: [
                { label: formatMessage("any"), value: "" },
                { label: formatMessage("yes"), value: "true" },
                { label: formatMessage("no"), value: "false" },
            ],
        },
    ];

    return [{ id: "main", title: "filters", filters: main }];
}
